import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  
  // States
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [instagram, setInstagram] = useState('');
  const [facebook, setFacebook] = useState('');
  const [linkedin, setLinkedin] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const docRef = doc(db, 'settings', 'global');
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          setIsMaintenance(data.isMaintenance || false);
          setPhone(data.phone || '');
          setEmail(data.email || '');
          setAddress(data.address || '');
          setInstagram(data.instagram || '');
          setFacebook(data.facebook || '');
          setLinkedin(data.linkedin || '');
        }
      } catch (err) {
        console.error("Error fetching settings:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSaveAll = async () => {
    setSaving(true);
    setMessage('');
    try {
      const docRef = doc(db, 'settings', 'global');
      await setDoc(docRef, {
        isMaintenance,
        phone,
        email,
        address,
        instagram,
        facebook,
        linkedin,
        updatedAt: new Date()
      }, { merge: true });
      setMessage('Tüm ayarlar başarıyla veritabanına kaydedildi! 🚀');
      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      console.error(err);
      setMessage('Hata: Ayarlar kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-zinc-500">Veriler yükleniyor...</div>;

  return (
    <div className="space-y-6 pb-20">
      <div className="mb-4">
        <h2 className="text-2xl font-black text-white" style={{ fontFamily: 'Raleway, sans-serif' }}>Sistem & Site Ayarları</h2>
        <p className="text-zinc-400 text-sm mt-1">Sitenin genel çalışmasını ve iletişim verilerini yönetin.</p>
      </div>

      {message && (
         <motion.div 
           initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
           className={`p-4 rounded-xl font-bold text-sm ${message.includes('Hata') ? 'bg-red-500/10 text-red-500 border border-red-500/50' : 'bg-green-500/10 text-green-500 border border-green-500/50'}`}
         >
           {message}
         </motion.div>
      )}

      {/* KRİTİK AYARLAR */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
        <div className="bg-red-600/10 border-b border-red-600/20 p-4 flex items-center gap-3">
          <i className="ri-alert-fill text-red-500"></i>
          <h3 className="font-bold text-red-500">Kritik Site Kontrolleri</h3>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between p-4 bg-zinc-950 rounded-xl border border-zinc-800">
            <div>
              <h4 className="text-white font-bold mb-1">Ziyaretçilere Siteyi Kapat (Bakım Modu)</h4>
              <p className="text-xs text-zinc-500">Bu açıldığında siteye giren müşteriler "Sitemiz Bakımdadır" yazısı görecektir. Sadece sen panelden erişebilirsin.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" checked={isMaintenance} onChange={(e) => setIsMaintenance(e.target.checked)} />
              <div className="w-14 h-7 bg-zinc-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-red-600"></div>
            </label>
          </div>
        </div>
      </motion.div>

      {/* İLETİŞİM BİLGİLERİ */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
        <h3 className="font-bold text-white mb-6 border-b border-zinc-800 pb-2">Kurumsal İletişim</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-zinc-400 text-xs font-bold mb-2 uppercase tracking-wider">Firma Telefonu</label>
            <input type="text" value={phone} onChange={e => setPhone(e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 text-white rounded-xl py-3 px-4 focus:border-red-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-zinc-400 text-xs font-bold mb-2 uppercase tracking-wider">E-Posta Adresi</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 text-white rounded-xl py-3 px-4 focus:border-red-500 focus:outline-none" />
          </div>
        </div>
        
        <div className="mt-6">
          <label className="block text-zinc-400 text-xs font-bold mb-2 uppercase tracking-wider">Merkez Adres</label>
          <textarea value={address} onChange={e => setAddress(e.target.value)} rows={3} className="w-full bg-zinc-950 border border-zinc-800 text-white rounded-xl py-3 px-4 focus:border-red-500 focus:outline-none"></textarea>
        </div>
      </motion.div>

      {/* SOSYAL MEDYA */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
        <h3 className="font-bold text-white mb-6 border-b border-zinc-800 pb-2">Sosyal Medya Linkleri</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-zinc-400 text-xs font-bold mb-2 uppercase tracking-wider flex items-center gap-2"><i className="ri-instagram-line"></i> Instagram</label>
            <input type="text" value={instagram} onChange={e => setInstagram(e.target.value)} placeholder="https://instagram.com/..." className="w-full bg-zinc-950 border border-zinc-800 text-white rounded-xl py-3 px-4 focus:border-red-500 focus:outline-none placeholder-zinc-700" />
          </div>
          <div>
            <label className="block text-zinc-400 text-xs font-bold mb-2 uppercase tracking-wider flex items-center gap-2"><i className="ri-facebook-box-line"></i> Facebook</label>
            <input type="text" value={facebook} onChange={e => setFacebook(e.target.value)} placeholder="https://facebook.com/..." className="w-full bg-zinc-950 border border-zinc-800 text-white rounded-xl py-3 px-4 focus:border-red-500 focus:outline-none placeholder-zinc-700" />
          </div>
          <div>
            <label className="block text-zinc-400 text-xs font-bold mb-2 uppercase tracking-wider flex items-center gap-2"><i className="ri-linkedin-box-line"></i> LinkedIn</label>
            <input type="text" value={linkedin} onChange={e => setLinkedin(e.target.value)} placeholder="https://linkedin.com/in/..." className="w-full bg-zinc-950 border border-zinc-800 text-white rounded-xl py-3 px-4 focus:border-red-500 focus:outline-none placeholder-zinc-700" />
          </div>
        </div>
      </motion.div>

      {/* DEV KAYDET BUTONU */}
      <div className="fixed bottom-0 left-0 right-0 md:left-64 bg-zinc-950/80 backdrop-blur-lg border-t border-zinc-800 p-4 flex justify-end z-50">
         <button 
           onClick={handleSaveAll} 
           disabled={saving}
           className="bg-red-600 hover:bg-red-700 text-white font-black text-lg py-4 px-12 rounded-xl shadow-2xl shadow-red-600/30 transition-all flex items-center gap-3 disabled:opacity-50"
         >
           {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-3-line"></i>}
           TÜMÜNÜ KAYDET VE YAYINLA
         </button>
      </div>

    </div>
  );
}
